/**
 * Reusable integration setup component with DIY and Remote Access options
 * Provides a consistent interface for all integration setups
 */

import React, { useState, ReactNode } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { 
  Wrench, 
  Users, 
  Save, 
  CheckCircle, 
  AlertCircle, 
  Clock,
  ExternalLink,
  Copy,
  Check
} from 'lucide-react';

import { OnboardingStatus, SetupMethod } from '@/types/onboarding';
import { toast } from '@/hooks/use-toast';

interface GuideStep {
  title: string;
  content: ReactNode;
  links?: Array<{
    title: string;
    url: string;
  }>;
}

interface IntegrationSetupProps {
  title: string;
  description: string;
  icon: ReactNode;
  status: OnboardingStatus;
  setupMethod?: SetupMethod;
  
  // DIY Setup
  diySteps?: GuideStep[];
  diyRequirements?: string[];
  customDiyContent?: ReactNode;
  
  // Remote Access
  remoteDescription?: string;
  remoteInstructions?: string[];
  remoteFormContent?: ReactNode;
  customRemoteContent?: ReactNode;
  
  // Callbacks
  onSetupMethodChange: (method: SetupMethod) => void;
  onSave: () => Promise<void>;
  onMarkComplete: () => Promise<void>;
  
  // States
  isSaving?: boolean;
  isMarkingComplete?: boolean;
  hasUnsavedChanges?: boolean;
  canMarkComplete?: boolean;
}

const IntegrationSetup: React.FC<IntegrationSetupProps> = ({
  title,
  description,
  icon,
  status,
  setupMethod = SetupMethod.DIY,
  diySteps = [],
  diyRequirements = [],
  customDiyContent,
  remoteDescription,
  remoteInstructions = [],
  remoteFormContent,
  customRemoteContent,
  onSetupMethodChange,
  onSave,
  onMarkComplete,
  isSaving = false,
  isMarkingComplete = false,
  hasUnsavedChanges = false,
  canMarkComplete = false,
}) => {
  const [activeTab, setActiveTab] = useState<string>(setupMethod);
  const [copiedValues, setCopiedValues] = useState<Set<string>>(new Set());

  // Handle tab change
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    onSetupMethodChange(value as SetupMethod);
  };

  // Copy to clipboard utility
  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedValues(prev => new Set(prev).add(label));
      toast({
        title: "Copied!",
        description: `${label} copied to clipboard`,
      });
      setTimeout(() => {
        setCopiedValues(prev => {
          const next = new Set(prev);
          next.delete(label);
          return next;
        });
      }, 2000);
    } catch (error) {
      toast({
        title: "Copy failed",
        description: "Please copy manually",
        variant: "destructive",
      });
    }
  };

  // Status badge component
  const StatusBadge = () => {
    switch (status) {
      case OnboardingStatus.COMPLETE:
        return <Badge className="bg-green-100 text-green-800">Complete</Badge>;
      case OnboardingStatus.AWAITING_CONFIRMATION:
        return <Badge className="bg-yellow-100 text-yellow-800">Under Review</Badge>;
      case OnboardingStatus.NEEDS_ATTENTION:
        return <Badge className="bg-red-100 text-red-800">Needs Attention</Badge>;
      default:
        return <Badge variant="secondary">Not Started</Badge>;
    }
  };

  // Render copyable value
  const CopyableValue = ({ value, label }: { value: string; label: string }) => {
    const isCopied = copiedValues.has(label);
    
    return (
      <div className="flex items-center gap-2 p-2 bg-gray-100 rounded border">
        <code className="flex-1 text-sm font-mono">{value}</code>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => copyToClipboard(value, label)}
          className="h-6 w-6 p-0"
        >
          {isCopied ? (
            <Check className="h-3 w-3 text-green-600" />
          ) : (
            <Copy className="h-3 w-3" />
          )}
        </Button>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {icon}
          <div>
            <h2 className="text-xl font-semibold">{title}</h2>
            <p className="text-sm text-muted-foreground">{description}</p>
          </div>
        </div>
        <StatusBadge />
      </div>

      {/* Status alerts */}
      {status === OnboardingStatus.NEEDS_ATTENTION && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            This integration needs your attention. Please review the feedback and make necessary changes.
          </AlertDescription>
        </Alert>
      )}

      {status === OnboardingStatus.AWAITING_CONFIRMATION && (
        <Alert>
          <Clock className="h-4 w-4" />
          <AlertDescription>
            Your setup has been submitted for review. Our team will verify the configuration and contact you if needed.
          </AlertDescription>
        </Alert>
      )}

      {status === OnboardingStatus.COMPLETE && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700">
            This integration has been successfully set up and verified.
          </AlertDescription>
        </Alert>
      )}

      {/* Setup method tabs */}
      <Card>
        <CardContent className="p-6">
          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value={SetupMethod.DIY} className="flex items-center gap-2 data-[state=active]:text-white group">
                <Wrench className="h-4 w-4 group-data-[state=active]:text-white group-data-[state=active]:stroke-white" />
                DIY Setup
              </TabsTrigger>
              <TabsTrigger value={SetupMethod.REMOTE_ACCESS} className="flex items-center gap-2 data-[state=active]:text-white group">
                <Users className="h-4 w-4 group-data-[state=active]:text-white group-data-[state=active]:stroke-white" />
                Remote Access
              </TabsTrigger>
            </TabsList>

            {/* DIY Setup Tab */}
            <TabsContent value={SetupMethod.DIY} className="space-y-6 mt-6">
              {/* Use custom DIY content if provided, otherwise use standard format */}
              {customDiyContent ? (
                customDiyContent
              ) : (
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
                    <Wrench className="h-5 w-5 text-blue-600 mt-1" />
                    <div>
                      <h4 className="font-medium text-blue-900">Do It Yourself</h4>
                      <p className="text-sm text-blue-700">
                        Follow our step-by-step guide to set up this integration yourself. 
                        You'll have full control over the process.
                      </p>
                    </div>
                  </div>

                  {/* Requirements */}
                  {diyRequirements.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-medium">What you'll need:</h4>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        {diyRequirements.map((requirement, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <span className="text-primary">•</span>
                            {requirement}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Setup steps */}
                  {diySteps.length > 0 && (
                    <div className="space-y-4">
                      <h4 className="font-medium">Setup Steps:</h4>
                      <Accordion type="single" collapsible className="w-full">
                        {diySteps.map((step, index) => (
                          <AccordionItem key={index} value={`step-${index}`}>
                            <AccordionTrigger className="text-left">
                              <span className="font-medium">
                                Step {index + 1}: {step.title}
                              </span>
                            </AccordionTrigger>
                            <AccordionContent className="space-y-4">
                              <div className="prose prose-sm max-w-none">
                                {step.content}
                              </div>
                              
                              {/* External links */}
                              {step.links && step.links.length > 0 && (
                                <div className="space-y-2">
                                  <h5 className="font-medium text-sm">Helpful Links:</h5>
                                  <div className="space-y-1">
                                    {step.links.map((link, linkIndex) => (
                                      <a
                                        key={linkIndex}
                                        href={link.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 text-sm text-primary hover:underline"
                                      >
                                        <ExternalLink className="h-3 w-3" />
                                        {link.title}
                                      </a>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </div>
                  )}

                  {diySteps.length === 0 && (
                    <div className="p-4 bg-gray-50 rounded-lg text-center">
                      <p className="text-sm text-muted-foreground">
                        DIY setup guide will be available soon. Please use Remote Access for now.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            {/* Remote Access Tab */}
            <TabsContent value={SetupMethod.REMOTE_ACCESS} className="space-y-6 mt-6">
              {/* Use custom remote content if provided, otherwise use standard format */}
              {customRemoteContent ? (
                customRemoteContent
              ) : (
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg">
                    <Users className="h-5 w-5 text-green-600 mt-1" />
                    <div>
                      <h4 className="font-medium text-green-900">Remote Access Setup</h4>
                      <p className="text-sm text-green-700">
                        {remoteDescription || 
                         "Our technical team will handle the setup for you. Just provide the necessary access credentials."}
                      </p>
                    </div>
                  </div>

                  {/* Instructions */}
                  {remoteInstructions.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-medium">Instructions:</h4>
                      <div className="space-y-4">
                        {remoteInstructions.map((instruction, index) => (
                          <div key={index} className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold bg-brand-blue text-white">
                              {index + 1}
                            </div>
                            <div className="flex-1">
                              <span className="text-sm text-muted-foreground">{instruction}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Form content */}
                  {remoteFormContent && (
                    <div className="space-y-4">
                      <h4 className="font-medium">Provide Access Details:</h4>
                      {remoteFormContent}
                    </div>
                  )}

                  {!remoteFormContent && remoteInstructions.length === 0 && (
                    <div className="p-4 bg-gray-50 rounded-lg text-center">
                      <p className="text-sm text-muted-foreground">
                        Remote access setup instructions will be provided by our team.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>
          </Tabs>

          {/* Action buttons */}
          <div className="flex items-center justify-between pt-6 border-t">
            <div className="flex items-center gap-2">
              {hasUnsavedChanges && activeTab === SetupMethod.REMOTE_ACCESS && (
                <div className="text-sm text-amber-600 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  You have unsaved changes
                </div>
              )}
            </div>
            
            <div className="flex gap-3">
              {activeTab === SetupMethod.REMOTE_ACCESS ? (
                <>
                  <Button
                    variant="outline"
                    onClick={onSave}
                    disabled={isSaving}
                    className="flex items-center gap-2"
                  >
                    <Save className="h-4 w-4" />
                    {isSaving ? 'Saving...' : 'Save Progress'}
                  </Button>

                  <Button
                    onClick={onMarkComplete}
                    disabled={!canMarkComplete || isMarkingComplete || hasUnsavedChanges}
                    className="flex items-center gap-2"
                  >
                    <CheckCircle className="h-4 w-4" />
                    {isMarkingComplete ? 'Submitting...' : 'Mark as Complete'}
                  </Button>
                </>
              ) : (
                <Button
                  onClick={onMarkComplete}
                  disabled={!canMarkComplete || isMarkingComplete}
                  className="flex items-center gap-2"
                >
                  <CheckCircle className="h-4 w-4" />
                  {isMarkingComplete ? 'Submitting...' : 'Mark as Complete'}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default IntegrationSetup;